/*
 * Regroups the Frengee product specifications table into themed card
 * sections (Frame & Suspension, Drivetrain, Brakes, ...) matching the
 * theme's card design language.
 *
 * Frengee renders one flat, alphabetically-sorted table client-side and
 * re-renders it on variant change, so this runs against the finished DOM
 * and re-runs via MutationObserver. Nothing is invented: every row keeps
 * its original label and value, only the order and grouping change.
 * Anything not matching a known group falls into "Other Details" rather
 * than being dropped.
 */
(function () {
  'use strict';

  // Group order + the spec labels that belong to each. Matching is
  // case-insensitive on the normalized label, so "Front Tire" matches
  // "front tire". Aliases cover the label variations different bike
  // products use for the same component.
  var GROUPS = [
    {
      name: 'Frame & Suspension',
      labels: ['frame', 'fork', 'rear shock', 'shock', 'suspension', 'size', 'frame size', 'headset']
    },
    {
      name: 'Drivetrain',
      labels: [
        'rear derailleur', 'front derailleur', 'derailleur', 'shift levers', 'shifters',
        'cassette', 'chain', 'crankset', 'crank', 'bottom bracket', 'chainring',
        'pedals', 'freewheel'
      ]
    },
    {
      name: 'Brakes',
      labels: ['front brake', 'rear brake', 'brakes', 'brake levers', 'brake']
    },
    {
      name: 'Wheels & Tires',
      labels: [
        'front wheel', 'rear wheel', 'wheels', 'wheelset', 'front hub', 'rear hub',
        'hubs', 'rims', 'rim', 'spokes', 'front tire', 'rear tire', 'tires', 'tire',
        'inner tubes', 'inner tube', 'tubes', 'valve'
      ]
    },
    {
      name: 'Cockpit',
      labels: [
        'handlebars', 'handlebar', 'bars', 'stem', 'grips', 'tape', 'bar tape',
        'seat post', 'seatpost', 'saddle', 'seat binder', 'seat clamp'
      ]
    },
    {
      name: 'Finish & Details',
      labels: [
        'color', 'primary colorway', 'colorway', 'secondary colorway', 'material',
        'gender', 'swat', 'finish', 'weight'
      ]
    }
  ];

  var FALLBACK_GROUP = 'Other Details';

  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function groupFor(label) {
    var key = normalize(label);
    for (var i = 0; i < GROUPS.length; i++) {
      if (GROUPS[i].labels.indexOf(key) !== -1) return GROUPS[i].name;
    }
    return FALLBACK_GROUP;
  }

  function readRows(table) {
    var rows = [];
    var trs = table.querySelectorAll('tr');
    for (var i = 0; i < trs.length; i++) {
      var cells = trs[i].cells;
      if (!cells || cells.length < 2) continue;
      var label = cells[0].textContent.replace(/\s+/g, ' ').trim();
      var value = cells[1].textContent.replace(/\s+/g, ' ').trim();
      if (!label && !value) continue;
      rows.push({ label: label, value: value });
    }
    return rows;
  }

  function buildSection(name, rows) {
    var section = document.createElement('div');
    section.className = 'spec-group';

    var header = document.createElement('div');
    header.className = 'spec-group__header';

    var title = document.createElement('h3');
    title.className = 'spec-group__title';
    title.textContent = name;

    header.appendChild(title);
    section.appendChild(header);

    var body = document.createElement('dl');
    body.className = 'spec-group__rows';

    rows.forEach(function (row) {
      var line = document.createElement('div');
      line.className = 'spec-row';

      var dt = document.createElement('dt');
      dt.className = 'spec-row__label';
      dt.textContent = row.label;

      var dd = document.createElement('dd');
      dd.className = 'spec-row__value';
      dd.textContent = row.value;

      line.appendChild(dt);
      line.appendChild(dd);
      body.appendChild(line);
    });

    section.appendChild(body);
    return section;
  }

  function render(table) {
    if (table.dataset.specCardsDone === '1') return;

    var rows = readRows(table);
    if (!rows.length) return;

    // Bucket rows, preserving the group order defined above and, within a
    // group, the order the labels are listed in.
    var buckets = {};
    rows.forEach(function (row) {
      var name = groupFor(row.label);
      (buckets[name] = buckets[name] || []).push(row);
    });

    var order = GROUPS.map(function (g) { return g.name; }).concat(FALLBACK_GROUP);

    var wrapper = document.createElement('div');
    wrapper.className = 'spec-groups';

    order.forEach(function (name) {
      var bucket = buckets[name];
      if (!bucket || !bucket.length) return;

      var group = GROUPS.filter(function (g) { return g.name === name; })[0];
      if (group) {
        bucket.sort(function (a, b) {
          var ia = group.labels.indexOf(normalize(a.label));
          var ib = group.labels.indexOf(normalize(b.label));
          if (ia === -1) ia = 999;
          if (ib === -1) ib = 999;
          return ia - ib;
        });
      }

      wrapper.appendChild(buildSection(name, bucket));
    });

    table.dataset.specCardsDone = '1';
    table.hidden = true;
    table.setAttribute('aria-hidden', 'true');

    // Drop any wrapper we built on a previous render pass.
    var previous = table.parentNode.querySelector(':scope > .spec-groups');
    if (previous) previous.remove();

    table.parentNode.insertBefore(wrapper, table);
  }

  function scan(root) {
    var tables = (root || document).querySelectorAll('.frengee-specs-table');
    for (var i = 0; i < tables.length; i++) render(tables[i]);
  }

  function start() {
    scan(document);

    var host = document.querySelector('.frengee-product-specifications-wrapper')
      || document.querySelector('[id*="product_specifications"]');
    if (!host) return;

    // Frengee re-renders on variant change and on its own async data load.
    var observer = new MutationObserver(function () {
      scan(host);
    });
    observer.observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
